import { ITEM_CARD_SEQUENCE_LIST_VIEW, RELATIONSHIP_VIEW, TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceUserActions from "../../constants/interfaceUserActions";
import { appStateActionTypes, appStateSelectors } from "../../modules/appState";
import { typeSelectors } from "../../modules/definition/types";
import { filterActionTypes } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { relationshipSelectors } from "../../modules/relationships";
import preprocessorQuery from "../../utils/preprocessorQuery";
import {
  getFlashPlaceholder,
  getIconPlaceholder,
  getRotatePortrait,
  getSwitchViewInstruction,
  getTipIdGenerator,
  Placeholders
} from "../utils";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "RELATE_PEOPLE_AND_PLACES";
const GROUP_LABEL = "Relate People and Places";
const GROUP_DESCRIPTION = `Setup ${Placeholders.ENTITY_PLURAL_LABEL} in your project and assign them to ${Placeholders.EVENT_PLURAL_LABEL}`;
const GROUP_GATE = state =>
  typeSelectors.getSidebarTypes(state).size > 0 && itemSelectors.getAllEventIds(state).size >= 5;
const GROUP_GATE_MESSAGE = `You must have an item type that is shown in the ${
  isMobile ? "toolbar" : "sidebar"
} and at least five events to begin this tutorial.`;
const GROUP_APPLICABLE_GATE = state => typeSelectors.getSidebarTypes(state).size > 0;
const GROUP_APPLICABLE_GATE_MESSAGE = `This tutorial only applies to timelines that have an item type that is shown in the ${
  isMobile ? "toolbar" : "sidebar"
}.`;
const GROUP_COMPLETED_MESSAGE = `Great work! You can now add other Project elements such as ${Placeholders.OTHER_ENTITY_TYPES} using the same technique.`;
const GROUP_LINKS = [
  {
    label: "Learn other ways to create relationships",
    path: "https://help.timeline.app/article/223-how-to-create-relationships-between-items"
  },
  { label: "Relationship View", path: "https://help.timeline.app/article/164-relationship-view" }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("ADD_ENTITY"),
      label: `Add ${Placeholders.ENTITY_PLURAL_LABEL} to your project`,
      steps: [
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by clicking its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_ADD,
            "+ Create button"
          )} and type a name for your ${Placeholders.ENTITY_LABEL}.`,
          isNumbered: true
        },
        { label: `Repeat to add further ${Placeholders.ENTITY_PLURAL_LABEL}.`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      actionInstruction: `Add at least 3 ${Placeholders.ENTITY_PLURAL_LABEL} to continue`,
      dismissalGate: state => {
        // At least 3 characters
        const sidebarTypes = typeSelectors.getSidebarTypes(state);
        const entityType = sidebarTypes && sidebarTypes.size ? sidebarTypes.first() : null;
        const itemCount = itemSelectors.getItemIdsOfType(state, entityType.id).size;
        return itemCount >= 3;
      }
    },
    {
      id: idGenerator("ADD_RELATIONSHIP_RELATIONSHIP"),
      label: `Assign ${Placeholders.ENTITY_PLURAL_LABEL} to your ${Placeholders.EVENT_PLURAL_LABEL}`,
      steps: [
        getSwitchViewInstruction(RELATIONSHIP_VIEW),
        {
          label: `${Placeholders.EVENT_PLURAL_LABEL} will be shown down the left hand side and entities along the top.`,
          isNumbered: true
        },
        {
          label: `Click on an intersection to see available relationship options between the two items`,
          isNumbered: true
        },
        { label: `Click on a relationship type to add or remove relationships`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      actionInstruction: `Add at least 3 relationships to continue`,
      dismissalGate: state => {
        // At least 3 relationships
        return relationshipSelectors.getRelationshipsById(state).size >= 3;
      }
    },
    {
      id: idGenerator("ADD_ENTITY_BIRTHDATES"),
      label: `Give your ${Placeholders.ENTITY_PLURAL_LABEL} a ${Placeholders.ENTITY_START_LABEL} to calculates ages`,
      steps: [
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by clicking its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        { label: `Click on ${Placeholders.AN_ENTITY_LABEL} to select it`, isNumbered: true },
        {
          label: `In the Inspector, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_DATES_TAB,
            "clock icon"
          )} ${getIconPlaceholder("inspectorTime")} to open the Date tab`,
          isNumbered: true
        },
        {
          label: `Change the ${
            Placeholders.ENTITY_START_LABEL
          } by typing a date or clicking on the calendar icon ${getIconPlaceholder("calendar")}`,
          isNumbered: true
        },
        {
          label: `${Placeholders.ENTITY_CAPITAL_LABEL} ages will be calculated and shown throughout the interface`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true
    },
    {
      id: idGenerator("ADD_RELATIONSHIP_INSPECTOR"),
      label: `View and add ${Placeholders.EVENT_LABEL} relationships in Inspector`,
      steps: [
        { label: `Click on ${Placeholders.AN_EVENT_LABEL} to select it`, isNumbered: true },
        {
          label: `In the Inspector, click on the ${getFlashPlaceholder(
            interfaceIdentifiers.INSPECTOR_RELATIONSHIPS_TAB,
            "person icon"
          )} ${getIconPlaceholder("inspectorRelationships")} to open the Relationships tab`,
          isNumbered: true
        },
        {
          label: `Relationships you have created will be shown, along with calculated ages where available`,
          isNumbered: true
        },
        {
          label: `Click on a + button next to a relationship type or "More Relationships" to create further relationships`,
          isNumbered: true
        }
      ],
      requiresInspector: true,
      accessGate: null,
      gateMessage: null,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_RELATIONSHIP_INSPECTOR
    },

    {
      id: idGenerator("RELATIONSHIP_FILTER_TIMELINE"),
      label: `View timelines for each ${Placeholders.ENTITY_LABEL}`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by clicking its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Click on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside one of your ${Placeholders.ENTITY_LABEL} names`,
          isNumbered: true
        },
        {
          label: `The timeline will be filtered to only show ${Placeholders.EVENT_PLURAL_LABEL} associated with that ${Placeholders.ENTITY_LABEL}`,
          isNumbered: true
        },
        {
          label: `Click the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === filterActionTypes.CHANGE_TYPE_REFERENCE_FILTER &&
        action.terms.length === 0
    }
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("ADD_ENTITY"),
      label: `Add ${Placeholders.ENTITY_PLURAL_LABEL} to your project`,
      steps: [
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_ADD,
            "+ Create button"
          )} and type a name for your ${Placeholders.ENTITY_LABEL} in the inspector`,
          isNumbered: true
        },
        {
          label: `Tap the back arrow, and repeat to add further ${Placeholders.ENTITY_PLURAL_LABEL}.`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true,
      actionInstruction: `Add at least 3 ${Placeholders.ENTITY_PLURAL_LABEL} to continue`,
      dismissalGate: state => {
        // At least 3 characters
        const sidebarTypes = typeSelectors.getSidebarTypes(state);
        const entityType = sidebarTypes && sidebarTypes.size ? sidebarTypes.first() : null;
        const itemCount = itemSelectors.getItemIdsOfType(state, entityType.id).size;
        return itemCount >= 3;
      }
    },
    {
      id: idGenerator("ADD_RELATIONSHIP_RELATIONSHIP"),
      label: `Assign ${Placeholders.ENTITY_PLURAL_LABEL} to your ${Placeholders.EVENT_PLURAL_LABEL}`,
      steps: [
        getSwitchViewInstruction(RELATIONSHIP_VIEW),
        {
          label: `${Placeholders.EVENT_PLURAL_LABEL} will be shown down the left hand side and entities along the top.`,
          isNumbered: true
        },
        {
          label: `Tap on an intersection to see available relationship options between the two items`,
          isNumbered: true
        },
        { label: `Tap on a relationship type to add or remove relationships`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      actionInstruction: `Add at least 3 relationships to continue`,
      dismissalGate: state => {
        // At least 3 relationships
        return relationshipSelectors.getRelationshipsById(state).size >= 3;
      }
    },
    {
      id: idGenerator("ADD_ENTITY_BIRTHDATES"),
      label: `Give your ${Placeholders.ENTITY_PLURAL_LABEL} a ${Placeholders.ENTITY_START_LABEL} to calculates ages`,
      steps: [
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Tap on ${Placeholders.AN_ENTITY_LABEL} to edit the ${Placeholders.ENTITY_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Date and Narrative"`, isNumbered: true },
        {
          label: `Tap on the date you want to edit, and either type a date or click on a date in the calendar picker`,
          isNumbered: true
        },
        {
          label: `${Placeholders.ENTITY_CAPITAL_LABEL} ages will be calculated and shown throughout the interface`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true,
      requiresExitEditMode: true
    },
    {
      id: idGenerator("ADD_RELATIONSHIP_INSPECTOR"),
      label: `View and add ${Placeholders.EVENT_LABEL} relationships in Inspector`,
      steps: [
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Relationships"`, isNumbered: true },
        {
          label: `Relationships you have created will be shown, along with calculated ages where available`,
          isNumbered: true
        },
        {
          label: `Tap on a + button next to a relationship type or "More Relationships" to create further relationships`,
          isNumbered: true
        }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_RELATIONSHIP_INSPECTOR
    },

    {
      id: idGenerator("RELATIONSHIP_FILTER_TIMELINE"),
      label: `View timelines for each ${Placeholders.ENTITY_LABEL}`,
      steps: [
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Tap on the ${getFlashPlaceholder(
            interfaceIdentifiers.ENTITY_PANEL_FILTER,
            "filter icon"
          )} ${getIconPlaceholder("workviewFilterFilled")} alongside one of your ${Placeholders.ENTITY_LABEL} names`,
          isNumbered: true
        },
        {
          label: `The timeline will be filtered to only show ${Placeholders.EVENT_PLURAL_LABEL} associated with that ${Placeholders.ENTITY_LABEL}`,
          isNumbered: true
        },
        {
          label: `Tap the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresExitEditMode: true,
      requiresInspector: true,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === filterActionTypes.CHANGE_TYPE_REFERENCE_FILTER &&
        action.terms.length === 0
    }
  ];
} else if (isMobile) {
  tips = [
    {
      id: idGenerator("ADD_ENTITY"),
      label: `Add ${Placeholders.ENTITY_PLURAL_LABEL} to your project`,
      steps: [
        getRotatePortrait(),
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the footer.`,
          isNumbered: true
        },
        {
          label: `Tap on the + Create button and type a name for your ${Placeholders.ENTITY_LABEL} in the inspector`,
          isNumbered: true
        },
        {
          label: `Tap the back arrow, and repeat to add further ${Placeholders.ENTITY_PLURAL_LABEL}.`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true,
      actionInstruction: `Add at least 3 ${Placeholders.ENTITY_PLURAL_LABEL} to continue`,
      dismissalGate: state => {
        // At least 3 characters
        const sidebarTypes = typeSelectors.getSidebarTypes(state);
        const entityType = sidebarTypes && sidebarTypes.size ? sidebarTypes.first() : null;
        const itemCount = itemSelectors.getItemIdsOfType(state, entityType.id).size;
        return itemCount >= 3;
      }
    },
    {
      id: idGenerator("ADD_RELATIONSHIP_INSPECTOR"),
      label: `View and add ${Placeholders.EVENT_LABEL} relationships in Inspector`,
      steps: [
        getSwitchViewInstruction(ITEM_CARD_SEQUENCE_LIST_VIEW),
        {
          label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit ${Placeholders.EVENT_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Relationships"`, isNumbered: true },
        {
          label: `Tap on a + button next to a relationship type or tap "More Relationships" to create further relationships`,
          isNumbered: true
        },
        {
          label: `Select the ${Placeholders.ENTITY_LABEL} that you wish to add to this ${Placeholders.EVENT_LABEL}`,
          isNumbered: true
        }
      ],
      requiresInspector: true,
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.CREATE_RELATIONSHIP_INSPECTOR
    },
    {
      id: idGenerator("ADD_ENTITY_BIRTHDATES"),
      label: `Give your ${Placeholders.ENTITY_PLURAL_LABEL} a ${Placeholders.ENTITY_START_LABEL} to calculates ages`,
      steps: [
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the footer.`,
          isNumbered: true
        },
        {
          label: `Tap on ${Placeholders.AN_ENTITY_LABEL} to edit the ${Placeholders.ENTITY_LABEL} details`,
          isNumbered: true
        },
        { label: `In the Inspector, tap on "Date and Narrative"`, isNumbered: true },
        {
          label: `Tap on the date you want to edit, and either type a date or click on a date in the calendar picker`,
          isNumbered: true
        },
        {
          label: `${Placeholders.ENTITY_CAPITAL_LABEL} ages will be calculated and shown throughout the interface`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true,
      requiresInspector: true,
      requiresExitEditMode: true
    },
    {
      id: idGenerator("RELATIONSHIP_FILTER_TIMELINE"),
      label: `View timelines for each ${Placeholders.ENTITY_LABEL}`,
      steps: [
        getRotatePortrait(),
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel by tapping its ${Placeholders.ENTITY_ICON_FLASH} ${Placeholders.ENTITY_ICON} in the sidebar.`,
          isNumbered: true
        },
        {
          label: `Tap on the filter icon ${getIconPlaceholder("workviewFilterFilled")} alongside one of your ${
            Placeholders.ENTITY_LABEL
          } names`,
          isNumbered: true
        },
        getSwitchViewInstruction(TIMELINE_VIEW),
        {
          label: `The timeline will be filtered to only show ${Placeholders.EVENT_PLURAL_LABEL} associated with that ${Placeholders.ENTITY_LABEL}`,
          isNumbered: true
        },
        {
          label: `Open the ${Placeholders.ENTITY_LABEL} panel and tap the active filter icon ${getIconPlaceholder(
            "workviewFilterFilledActive",
            "iconActive"
          )} again to remove the filter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresExitEditMode: true,
      requiresInspector: true,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
        action.type === filterActionTypes.CHANGE_TYPE_REFERENCE_FILTER &&
        action.terms.length === 0
    }
  ];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
